import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';

/**
 * Standard product-page hero used across the Videra product pages.
 * Keeps typography and CTA layout consistent across the rebuild.
 */
const ProductHero = ({
  eyebrow,
  title,
  subtitle,
  body,
  primary,      // { label, to?, href? }
  secondary,    // { label, to?, href? }
  supportingLine,
  heroImage,    // optional /images/... URL
  heroImageAlt = '',
  testid,
}) => {
  const renderCta = (cta, dark = true) => {
    if (!cta) return null;
    const cls = dark
      ? 'inline-flex items-center justify-center bg-slate-900 text-white px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group'
      : 'inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-7 py-3.5 rounded-md text-sm font-medium hover:border-slate-900 transition-colors';
    const inner = (
      <>
        {cta.label}
        {dark ? (
          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        ) : (
          <ChevronRight className="ml-1 w-4 h-4" />
        )}
      </>
    );
    if (cta.href) {
      return (
        <a href={cta.href} className={cls} data-testid={cta.testid}>
          {inner}
        </a>
      );
    }
    return (
      <Link to={cta.to} className={cls} data-testid={cta.testid}>
        {inner}
      </Link>
    );
  };

  const hasImage = Boolean(heroImage);

  return (
    <section className="relative pt-20 pb-16 md:pt-28 md:pb-20 overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className={`relative mx-auto px-4 sm:px-6 lg:px-8 ${hasImage ? 'max-w-7xl' : 'max-w-5xl'}`}>
        <div className={hasImage ? 'grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center' : ''}>
          <div>
            {eyebrow && (
              <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-5">{eyebrow}</p>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-5 text-lg lg:text-xl text-slate-500 leading-relaxed max-w-3xl">
                {subtitle}
              </p>
            )}
            {body && (
              <div className="mt-8 text-base lg:text-lg text-slate-700 leading-relaxed max-w-3xl space-y-4">
                {typeof body === 'string' ? <p>{body}</p> : body}
              </div>
            )}
            {(primary || secondary) && (
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                {renderCta(primary, true)}
                {renderCta(secondary, false)}
              </div>
            )}
            {supportingLine && (
              <p className="mt-6 text-sm text-slate-500">{supportingLine}</p>
            )}
          </div>
          {hasImage && (
            <div className="relative bg-slate-900 rounded-2xl p-2 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.45)] ring-1 ring-slate-900/5">
              <img
                src={heroImage}
                alt={heroImageAlt}
                loading="eager"
                className="w-full aspect-[16/11] object-cover object-top rounded-lg"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductHero;
